import { Box, Button, Tooltip } from '@mui/material'
import { useOperationsStore } from '@operations/AppProvider'
import { SplitButton } from '@operations/components/split-button/SplitButton'
import { CraneSplitContainerUIStore } from '@operations/features/craneSplit/stores/crane-split-container.ui-store'
import { CraneSplitDialogUIStore } from '@operations/features/craneSplit/stores/crane-split-dialog.ui-store'
import { SelectCranesDialogUIStore } from '@operations/features/craneSplit/stores/select-cranes-dialog.ui-store'
import { delay } from '@operations/utils/delay'
import { downloadHtmlAsJPG, downloadHtmlAsPDF } from '@operations/utils/export-html'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'

interface Props {
  uiDialogStore: CraneSplitDialogUIStore
  uiContainerStore: CraneSplitContainerUIStore
  uiSelectCranesDialogStore: SelectCranesDialogUIStore
}

export const CraneSplitActions = observer(
  ({ uiDialogStore, uiContainerStore, uiSelectCranesDialogStore }: Props) => {
    const navigate = useNavigate()
    const { t } = useTranslate()
    const { appStore } = useOperationsStore()

    const downloadHtmlAsFile = async (type: 'pdf' | 'image' = 'pdf') => {
      appStore.increaseLoadingCounter()
      uiContainerStore.setExporting(true)

      requestAnimationFrame(async () => {
        const craneSplitBaysElement = document.getElementById('crane-split-bays') as HTMLElement

        const hoursElement = document.getElementById('crane-split-hours') as HTMLElement
        const multiplier = document.getElementsByTagName('main').length ? 5 : 2

        if (type === 'pdf') {
          await downloadHtmlAsPDF(
            'crane-split-page',
            'crane-split',
            'landscape',
            craneSplitBaysElement.scrollWidth +
              (craneSplitBaysElement.scrollWidth === craneSplitBaysElement.offsetWidth
                ? hoursElement.scrollWidth
                : hoursElement.scrollWidth * multiplier),
          )
        } else {
          await downloadHtmlAsJPG(
            'crane-split-page',
            'crane-split',
            craneSplitBaysElement.scrollWidth +
              (craneSplitBaysElement.scrollWidth === craneSplitBaysElement.offsetWidth
                ? hoursElement.scrollWidth
                : hoursElement.scrollWidth * multiplier),
          )
        }

        uiContainerStore.setExporting(false)

        await delay(1000)
        appStore.decreaseLoadingCounter()
      })
    }

    const handleGoToWorkInstructions = () => {
      navigate(`${PATHS.controlRoom.jobs.url}?vesselVisitId=${uiContainerStore.vesselVisit?.id}`)
    }

    const handleGoToEquipments = () => {
      navigate(
        `${PATHS.visits.vesselVisits.url}/${uiContainerStore.vesselVisit?.id}/${PATHS.visits.vesselVisits.equipments.url}`,
      )
    }

    const options: ButtonOption[] = [
      {
        label: t('editCraneSplit', 'Edit crane split'),
        divider: true,
        onClick: () => {
          uiSelectCranesDialogStore.toggleWithSelectedCranes(uiContainerStore.craneIds)
        },
        testId: 'crane-split-more-options-edit-crane-split',
      },
      {
        label: t('editProcessingTime', 'Edit processing time'),
        divider: true,
        onClick: () => {
          uiDialogStore.toggleProcessingTime()
        },
        testId: 'crane-split-more-options-edit-processing-time',
      },
      {
        label: t('undo', 'Undo (Ctrl + Z)'),
        divider: true,
        disabled: !uiContainerStore.canUndo,
        onClick: () => {
          uiContainerStore.undo()
        },
      },
      {
        label: t('redo', 'Redo (Ctrl + Shift + Z)'),
        divider: true,
        disabled: !uiContainerStore.canRedo,
        onClick: () => {
          uiContainerStore.redo()
        },
      },
    ]

    const exportOptions: ButtonOption[] = [
      {
        label: t('exportAsPDF', 'Export as PDF'),
        divider: true,
        onClick: () => {
          downloadHtmlAsFile()
        },
      },
      {
        label: t('exportAsImage', 'Export as Image'),
        onClick: () => {
          downloadHtmlAsFile('image')
        },
      },
    ]

    useEffect(() => {
      const handleKeyboardEvents = (event: KeyboardEvent) => {
        const pressedControlZ = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' //meta is command for mac
        const dialogsAreClosed =
          !uiDialogStore.resetDialog &&
          !uiDialogStore.detailsDialog &&
          !uiDialogStore.removeDialog &&
          !uiDialogStore.editWorkQueueIsOpen &&
          !uiDialogStore.processingTimeIsOpen

        if (uiContainerStore.canUndo && dialogsAreClosed && pressedControlZ && !event.shiftKey) {
          event.preventDefault()
          uiContainerStore.undo()
        }

        if (uiContainerStore.canRedo && dialogsAreClosed && pressedControlZ && event.shiftKey) {
          event.preventDefault()
          uiContainerStore.redo()
        }
      }

      document.addEventListener('keydown', handleKeyboardEvents)

      return () => document.removeEventListener('keydown', handleKeyboardEvents)
    }, [uiContainerStore, uiDialogStore])

    return (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <SplitButton
          variant='outlined'
          color='inherit'
          size='large'
          options={exportOptions}
          disabled={!uiDialogStore.hasWorkQueues}
          label={t('export', 'Export')}
        />

        {uiDialogStore.isVisitOnTerminal ? (
          <>
            <Button variant='contained' color='inherit' onClick={handleGoToWorkInstructions}>
              {t('workInstructions', 'Work instructions')}
            </Button>
            <Button variant='contained' color='inherit' onClick={handleGoToEquipments}>
              {t('equipments', 'Equipments')}
            </Button>
          </>
        ) : (
          <Tooltip
            title={t(
              'workInstructionsAreAvailableWhenTheVesselIsOnTheTerminal',
              'Work instructions are available only when the vessel is on the terminal',
            )}
          >
            <Box height='100%'>
              <Button variant='contained' color='inherit' disabled sx={{ height: '100%' }}>
                {t('workInstructions', 'Work instructions')}
              </Button>
            </Box>
          </Tooltip>
        )}

        {uiContainerStore.canModify && (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            size='large'
            options={options}
            testId='crane-split-more-options-btn'
          />
        )}
      </Box>
    )
  },
)
